import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion';
import { useExperience, useScrollLock } from '@/lib/experience';
import { NAV_ITEMS, LOGO, PHOTOS, INSTAGRAM_URL, INSTAGRAM_HANDLE, LOCATION, whatsappLink, DEFAULT_MESSAGE } from '@/data/site';
import { Wordmark } from './brand/Wordmark';
import { WhatsAppIcon, InstagramIcon, Heart } from './brand/Shapes';
import { Magnetic } from './fx/Magnetic';
import { Photo } from './fx/Photo';

const MENU_COLORS = ['bg-pet-pink', 'bg-pet-peach', 'bg-pet-mint', 'bg-pet-sky', 'bg-pet-lilac', 'bg-pet-pink'];

export const Navbar = () => {
  const { scrollTo, ready } = useExperience();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [active, setActive] = useState('inicio');
  const { scrollY } = useScroll();

  useScrollLock(open);

  useMotionValueEvent(scrollY, 'change', (y) => {
    const prev = scrollY.getPrevious() ?? 0;
    if (y > 320 && y > prev + 2) setHidden(true);
    if (y < prev - 2) setHidden(false);
  });

  useEffect(() => {
    const els = document.querySelectorAll('[data-nav]');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.getAttribute('data-nav'));
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollTo(id === 'inicio' ? 'top' : id), 30);
  };

  const pill = 'bg-white/90 backdrop-blur-md shadow-[0_10px_30px_-12px_rgba(77,150,255,0.45)] ring-1 ring-pet-blue/10';

  return (
    <>
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[140] focus:px-4 focus:py-2 focus:bg-pet-blue focus:text-white focus:rounded-full"
      >
        Pular para o conteúdo
      </a>

      <motion.header
        className="fixed inset-x-0 top-0 z-[80] pointer-events-none"
        initial={{ y: -120 }}
        animate={{ y: ready && (!hidden || open) ? 0 : -120 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      >
        <div className="container-cp flex items-center justify-between gap-3 pt-3 md:pt-5">
          <motion.button
            type="button"
            onClick={() => go('inicio')}
            whileHover={{ rotate: -3, scale: 1.04 }}
            whileTap={{ scale: 0.94 }}
            className={`pointer-events-auto flex items-center gap-2.5 rounded-full pl-1.5 pr-4 py-1.5 ${open ? 'bg-white' : pill}`}
            aria-label="Cãopanhia Pet — ir para o início"
          >
            <img src={LOGO} alt="" className="w-10 h-10 md:w-11 md:h-11 rounded-full ring-2 ring-pet-pink" />
            <Wordmark className="text-[20px] md:text-[22px] text-pet-blue" />
          </motion.button>

          <nav aria-label="Navegação principal" className="pointer-events-auto hidden lg:block">
            <ul className={`flex items-center gap-1 rounded-full p-1.5 ${pill}`}>
              {NAV_ITEMS.map((item) => {
                const isActive = active === item.id;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => go(item.id)}
                      aria-current={isActive ? 'true' : undefined}
                      className={`roll-parent relative px-4 py-2 rounded-full text-[15px] font-extrabold transition-colors duration-300 ${
                        isActive ? 'text-white' : 'text-pet-ink hover:text-pet-blue'
                      }`}
                    >
                      {isActive && (
                        <motion.span
                          layoutId="nav-pill"
                          className="absolute inset-0 rounded-full bg-pet-blue"
                          transition={{ type: 'spring', stiffness: 420, damping: 22 }}
                        />
                      )}
                      <span className="roll relative">
                        <span>{item.label}</span>
                        <span aria-hidden="true">{item.label}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="pointer-events-auto flex items-center gap-2">
            <Magnetic className="hidden md:inline-flex">
              <a href={whatsappLink(DEFAULT_MESSAGE)} target="_blank" rel="noopener noreferrer" className="btn-blue min-h-[48px] pl-2 pr-5 text-[15px]">
                <span className="w-8 h-8 rounded-full bg-pet-whats flex items-center justify-center">
                  <WhatsAppIcon className="w-[18px] h-[18px]" />
                </span>
                Agendar
              </a>
            </Magnetic>
            <motion.button
              type="button"
              onClick={() => setOpen((v) => !v)}
              whileTap={{ scale: 0.88 }}
              aria-expanded={open}
              aria-controls="menu-mobile"
              aria-label={open ? 'Fechar menu' : 'Abrir menu'}
              className={`lg:hidden relative w-12 h-12 rounded-full flex items-center justify-center ${open ? 'bg-white text-pet-pink-deep' : `${pill} text-pet-blue`}`}
            >
              <span className="sr-only">Menu</span>
              <motion.span
                className="absolute h-[3px] w-5 rounded-full bg-current"
                animate={open ? { rotate: 45, y: 0 } : { rotate: 0, y: -4 }}
                transition={{ type: 'spring', stiffness: 500, damping: 20 }}
              />
              <motion.span
                className="absolute h-[3px] w-5 rounded-full bg-current"
                animate={open ? { rotate: -45, y: 0 } : { rotate: 0, y: 4 }}
                transition={{ type: 'spring', stiffness: 500, damping: 20 }}
              />
            </motion.button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="menu-mobile"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            data-lenis-prevent
            className="fixed inset-0 z-[75] bg-pet-blue text-white overflow-y-auto"
            initial={{ clipPath: 'circle(0% at calc(100% - 44px) 44px)' }}
            animate={{ clipPath: 'circle(150% at calc(100% - 44px) 44px)' }}
            exit={{ clipPath: 'circle(0% at calc(100% - 44px) 44px)' }}
            transition={{ duration: 0.7, ease: [0.65, 0, 0.35, 1] }}
          >
            <div className="container-cp min-h-full flex flex-col pt-28 pb-8">
              <ul className="flex flex-col gap-2.5">
                {NAV_ITEMS.map((item, i) => (
                  <motion.li
                    key={item.id}
                    initial={{ x: 60, opacity: 0, rotate: 6 }}
                    animate={{ x: 0, opacity: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 320, damping: 18, delay: 0.12 + i * 0.05 }}
                  >
                    <button
                      type="button"
                      onClick={() => go(item.id)}
                      className="w-full flex items-center gap-4 rounded-[22px] bg-white/10 hover:bg-white/20 px-4 py-3 text-left transition-colors"
                    >
                      <span className={`w-10 h-10 shrink-0 rounded-full ${MENU_COLORS[i]} text-pet-ink font-display text-lg flex items-center justify-center`}>
                        {i + 1}
                      </span>
                      <span className="font-display text-[2rem] leading-none">{item.label}</span>
                      {active === item.id && <Heart className="w-5 h-5 ml-auto text-pet-pink heartbeat" />}
                    </button>
                  </motion.li>
                ))}
              </ul>

              <motion.div
                className="mt-auto pt-10 flex items-end justify-between gap-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, type: 'spring', stiffness: 260, damping: 20 }}
              >
                <div className="flex flex-col gap-3">
                  <a href={whatsappLink(DEFAULT_MESSAGE)} target="_blank" rel="noopener noreferrer" className="btn-white whitespace-nowrap pl-2.5">
                    <span className="w-9 h-9 rounded-full bg-pet-whats text-white flex items-center justify-center">
                      <WhatsAppIcon className="w-5 h-5" />
                    </span>
                    Falar no WhatsApp
                  </a>
                  <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-white/90">
                    <InstagramIcon className="w-4 h-4" /> {INSTAGRAM_HANDLE}
                  </a>
                  <span className="text-sm font-bold text-white/70">📍 {LOCATION}</span>
                </div>
                <motion.div
                  className="relative w-24 shrink-0 rounded-2xl overflow-hidden ring-4 ring-white shadow-2xl"
                  initial={{ rotate: 20, scale: 0 }}
                  animate={{ rotate: 6, scale: 1 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 12, delay: 0.5 }}
                >
                  <Photo photo={PHOTOS.oculosEmPe} sizes="96px" className="w-full aspect-[9/16] object-cover" alt="" />
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
